// frontend/source/portal/components/CreateArtworkCard.js
// Modified version to support both category/tag and artist modes

import React, { useState, useEffect } from "react";
import ImagePicker from "./ImagePicker";
import ConfirmDialog from "./ConfirmDialog";
import ArtworkPickerModal from "./ArtworkPickerModal";
import adminApi from "../../services/adminapi";
import "./CreateArtworkCard.css";

const CreateArtworkCard = ({
  onCreate,
  onAddExisting,
  categoryId,
  tagId,
  artistId, // New prop for artist mode
}) => {
  const [mode, setMode] = useState(null); // null, 'create', 'existing'
  const [title, setTitle] = useState("");
  const [mediumId, setMediumId] = useState("");
  const [date, setDate] = useState("");
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [mediums, setMediums] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showArtworkPicker, setShowArtworkPicker] = useState(false);

  useEffect(() => {
    loadMediums();
  }, []);

  const loadMediums = async () => {
    try {
      const data = await adminApi.getMediums();
      setMediums(data);
    } catch (err) {
      console.error("Failed to load mediums:", err);
    }
  };

  const handleStartCreate = () => {
    setMode("create");
  };

  const handleStartAddExisting = () => {
    setShowArtworkPicker(true);
  };

  const handleCancel = () => {
    if (
      title.trim() !== "" ||
      mediumId !== "" ||
      date !== "" ||
      width !== "" ||
      height !== "" ||
      selectedImage
    ) {
      setShowCancelConfirm(true);
    } else {
      resetForm();
    }
  };

  const confirmCancel = () => {
    resetForm();
    setShowCancelConfirm(false);
  };

  const resetForm = () => {
    setMode(null);
    setTitle("");
    setMediumId("");
    setDate("");
    setWidth("");
    setHeight("");
    setSelectedImage(null);
  };

  const handleSave = async () => {
    if (title.trim() === "") {
      alert("Artwork title cannot be empty");
      return;
    }

    if (!selectedImage) {
      alert("Please select an image for the artwork");
      return;
    }

    try {
      setIsSaving(true);
      const artworkData = {
        title: title.trim(),
        image: selectedImage,
      };
      console.log("Create Artwork Card handleSave with categoryId", categoryId);

      // Add category/tag data if provided
      if (categoryId) artworkData.categoryId = categoryId;
      if (tagId) artworkData.tags = [tagId];

      // Add artist data if provided
      if (artistId) artworkData.artistId = artistId;

      if (mediumId) artworkData.mediumId = mediumId;
      if (date) artworkData.date = date;
      if (width) artworkData.width = parseFloat(width);
      if (height) artworkData.height = parseFloat(height);

      await onCreate(artworkData);
      resetForm();
    } catch (err) {
      console.error("Failed to create artwork:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddExistingArtwork = async (artworkId) => {
    try {
      await onAddExisting(artworkId);
      setShowArtworkPicker(false);
    } catch (err) {
      console.error("Failed to add existing artwork:", err);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  const handleImageSelect = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  if (mode === null) {
    return (
      <>
        <div className="create-artwork-card">
          <button className="create-artwork-option" onClick={handleStartCreate}>
            <div className="create-artwork-icon">+</div>
            <h3 className="create-artwork-text">Create New</h3>
          </button>
          <div className="create-artwork-divider">or</div>
          <button
            className="create-artwork-option"
            onClick={handleStartAddExisting}
          >
            <div className="create-artwork-icon">📎</div>
            <h3 className="create-artwork-text">Add Existing</h3>
          </button>
        </div>

        {showArtworkPicker && (
          <ArtworkPickerModal
            onSelect={handleAddExistingArtwork}
            onClose={() => setShowArtworkPicker(false)}
            categoryId={categoryId}
            currentTagId={tagId}
            artistId={artistId}
          />
        )}
      </>
    );
  }

  return (
    <div className="create-artwork-card creating">
      <div className="create-artwork-card-image-container">
        {selectedImage ? (
          <>
            <img
              src={selectedImage}
              alt="Selected artwork"
              className="create-artwork-card-image"
            />
            <button
              onClick={() => setShowImagePicker(true)}
              className="create-artwork-change-image-btn"
              title="Change image"
              disabled={isSaving}
            >
              📷
            </button>
          </>
        ) : (
          <div
            className="create-artwork-image-placeholder"
            onClick={() => setShowImagePicker(true)}
          >
            <div className="create-artwork-image-icon">🖼️</div>
            <p>Click to select image</p>
          </div>
        )}
      </div>

      <div className="create-artwork-form">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={handleKeyPress}
          className="create-artwork-input title-input"
          placeholder="Title"
          autoFocus
          disabled={isSaving}
        />
        <select
          value={mediumId}
          onChange={(e) => setMediumId(e.target.value)}
          className="create-artwork-input medium-select"
          disabled={isSaving}
        >
          <option value="">Select Medium (optional)</option>
          {mediums.map((medium) => (
            <option key={medium._id} value={medium._id}>
              {medium.name}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          onKeyDown={handleKeyPress}
          className="create-artwork-input date-input"
          disabled={isSaving}
        />
        <div className="create-artwork-dimensions">
          <input
            type="number"
            value={width}
            onChange={(e) => setWidth(e.target.value)}
            onKeyDown={handleKeyPress}
            className="create-artwork-input dimension-input"
            placeholder="Width (cm)"
            step="0.1"
            min="0"
            disabled={isSaving}
          />
          <span className="dimension-separator">×</span>
          <input
            type="number"
            value={height}
            onChange={(e) => setHeight(e.target.value)}
            onKeyDown={handleKeyPress}
            className="create-artwork-input dimension-input"
            placeholder="Height (cm)"
            step="0.1"
            min="0"
            disabled={isSaving}
          />
        </div>
        <div className="create-artwork-buttons">
          <button
            onClick={handleSave}
            className="create-artwork-btn save-btn"
            disabled={isSaving || !title.trim() || !selectedImage}
          >
            {isSaving ? "Creating..." : "Create"}
          </button>
          <button
            onClick={handleCancel}
            className="create-artwork-btn cancel-btn"
            disabled={isSaving}
          >
            Cancel
          </button>
        </div>
      </div>

      {showImagePicker && (
        <ImagePicker
          currentImage={selectedImage}
          onImageSelect={handleImageSelect}
          onClose={() => setShowImagePicker(false)}
        />
      )}

      <ConfirmDialog
        isOpen={showCancelConfirm}
        title="Discard Changes?"
        message="You have unsaved changes. Are you sure you want to cancel creating this artwork?"
        confirmText="Discard"
        cancelText="Keep Editing"
        type="warning"
        onConfirm={confirmCancel}
        onCancel={() => setShowCancelConfirm(false)}
      />
    </div>
  );
};

export default CreateArtworkCard;
